import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material'
import { lessonWordsApi } from '@/api/lessonWords'
import type { LessonParticipant, LessonWordAddResult } from '@/types'
import { getErrorMessage } from '@/utils/error'

type LessonWordAddFormProps = {
  lessonId: number
  participants: LessonParticipant[]
  onAdded: (result: LessonWordAddResult & { message?: string }) => void
}

export function LessonWordAddForm({ lessonId, participants, onAdded }: LessonWordAddFormProps) {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    const trimmedText = text.trim()
    if (!trimmedText) {
      setError(t('lessonWords.validation.textRequired', 'Введите слово'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const studentIds = participants.map((p) => p.user_id).filter((id) => id > 0)
      const result = await lessonWordsApi.add(lessonId, {
        student_ids: studentIds.length > 0 ? studentIds : undefined,
        text: trimmedText,
        translations: [],
      })
      onAdded(result)
      setText('')
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label={t('lessonWords.form.original', 'Слово')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          size="small"
          fullWidth
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void handleSubmit()
            }
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => void handleSubmit()}
          disabled={loading}
          sx={{ height: 40, flexShrink: 0 }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('lessonWords.form.add', 'Добавить слово')}
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  )
}

export function LessonWordSkippedAlert({
  skipped,
}: {
  skipped: Array<{ text: string; reason: string }>
}) {
  const { t } = useTranslation()
  if (skipped.length === 0) return null

  return (
    <Alert severity="info">
      {t('lessonWords.skippedIntro', 'Некоторые слова не были добавлены повторно:')}
      <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
        {skipped.map((item, index) => (
          <Box component="li" key={`${item.text}-${index}`}>
            {t('lessonWords.skippedItem', '{{text}} — уже на уроке', { text: item.text })}
          </Box>
        ))}
      </Box>
    </Alert>
  )
}

export function LessonWordCardAlreadyExistedAlert({ texts }: { texts: string[] }) {
  const { t } = useTranslation()
  if (texts.length === 0) return null

  return (
    <Alert severity="warning">
      {t('lessonWords.cardExistedIntro', 'У ученика уже были карточки на эти слова:')}
      <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
        {texts.map((text, index) => (
          <Box component="li" key={`${text}-${index}`}>
            {text}
          </Box>
        ))}
      </Box>
    </Alert>
  )
}
